import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { post } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn, dateOnly, money } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { Alert, Badge, Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { LoadMore, useCursorList } from './cursor-list';
import { canVoidReceipt, isVoidedReceipt } from './inventory-view';
import { PrintDocumentButton } from './PrintDocumentButton';
import { ReasonDialog } from './ReasonDialog';

const GRN_PAGE_SIZE = 50;

interface GoodsReceivedNote {
  id: string;
  number: string;
  supplier: { id: string; name: string };
  store: { id: string; name: string } | null;
  supplierInvoiceNo: string | null;
  receivedAt: string;
  subtotalMinor: number;
  taxMinor: number;
  discountMinor: number;
  totalMinor: number;
  items: unknown[];
  voidedAt: string | null;
  voidReason: string | null;
}

export function GoodsReceivedTab({ storeId }: { storeId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [voiding, setVoiding] = useState<GoodsReceivedNote | null>(null);
  const mayManage = can('purchase.manage');

  const refreshAfterVoid = () => {
    void queryClient.invalidateQueries({ queryKey: ['grns'] });
    void queryClient.invalidateQueries({ queryKey: ['stock'] });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
    void queryClient.invalidateQueries({ queryKey: ['payables'] });
  };

  const receipts = useCursorList<GoodsReceivedNote>({
    queryKey: ['grns', storeId],
    path: '/pharmacy/grns',
    params: { storeId },
    pageSize: GRN_PAGE_SIZE,
    enabled: true,
  });

  return (
    <>
      <DataTable
        rows={receipts.rows}
        loading={receipts.isLoading}
        error={receipts.firstPageError}
        rowKey={(row) => row.id}
        empty={tr('pharmacy', 'no_goods_have_been_received_yet_use_receive')}
        footer={
          receipts.hasMore || receipts.nextPageError ? (
            <div className="w-full">
              <LoadMore hasMore={receipts.hasMore} loading={receipts.loadingMore} error={receipts.nextPageError} onLoad={receipts.loadMore} />
            </div>
          ) : undefined
        }
        columns={[
          {
            header: tr('pharmacy', 'number'),
            sort: (row) => row.number,
            cell: (row) => (
              <div>
                <p className="font-mono text-xs">{row.number}</p>
                <p className="text-xs text-muted-foreground">{dateOnly(row.receivedAt)}</p>
              </div>
            ),
          },
          { header: tr('pharmacy', 'supplier'), sort: (row) => row.supplier.name, cell: (row) => row.supplier.name },
          { header: tr('pharmacy', 'store'), sort: (row) => row.store?.name, cell: (row) => row.store?.name ?? '—' },
          { header: tr('pharmacy', 'supplier_invoice'), sort: (row) => row.supplierInvoiceNo, cell: (row) => row.supplierInvoiceNo || '—' },
          {
            header: tr('pharmacy', 'total'),
            align: 'right',
            sort: (row) => row.totalMinor,
            cell: (row) => (
              <span className={cn(isVoidedReceipt(row) && 'text-muted-foreground line-through')}>{money(row.totalMinor)}</span>
            ),
          },
          {
            header: tr('pharmacy', 'status'),
            sort: (row) => isVoidedReceipt(row),
            cell: (row) =>
              isVoidedReceipt(row) ? (
                <div>
                  <Badge variant="destructive">{tr('pharmacy', 'voided')}</Badge>
                  {row.voidReason && <p className="mt-0.5 text-xs text-muted-foreground">{row.voidReason}</p>}
                </div>
              ) : (
                <Badge variant="success">{tr('pharmacy', 'received')}</Badge>
              ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                <PrintDocumentButton path={`/pharmacy/print/grn/${row.id}`} label={tr('pharmacy', 'print_goods_received_note')} compact />
                {canVoidReceipt(row, mayManage) && (
                  <Button size="sm" variant="ghost" onClick={() => setVoiding(row)}>
                    {tr('pharmacy', 'void')}
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      {voiding && (
        <ReasonDialog
          title={tr('pharmacy', 'void_x', { number: voiding.number })}
          description={`${voiding.supplier.name}, ${money(voiding.totalMinor)}`}
          confirmLabel={tr('pharmacy', 'void_goods_received')}
          keepLabel="Keep it"
          consequence={
            <Alert variant="warning">
              {tr('pharmacy', 'the_stock_this_receipt_brought_in_is_taken_back')}
            </Alert>
          }
          run={(reason, idempotencyKey) => post(`/pharmacy/grns/${voiding.id}/void`, { reason }, { idempotencyKey })}
          onDone={() => {
            toast.success(tr('pharmacy', 'goods_received_voided'), voiding.number);
            refreshAfterVoid();
            setVoiding(null);
          }}
          onClose={() => setVoiding(null)}
        />
      )}
    </>
  );
}
